import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '../ui/sheet';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { PaymentDialog } from './PaymentDialog';
import { DiscountControl } from './DiscountControl';
import { ItemEditDialog } from './ItemEditDialog';
import { StornoReasonDialog } from './StornoReasonDialog';
import { StatusPill } from './StatusPill';
import { KITCHEN_STATUS, kitchenStatusLabel, fmtEur } from '../../lib/constants';
import { claimAndPrintComande } from '../../lib/comande';
import { cn } from '../../lib/utils';
import { useConfirm } from '../../context/ConfirmContext';
import { useAuth } from '../../context/AuthContext';
import { CategoryIcon } from '../../lib/categoryIcons';
import { requestBillAndPrint } from '../../lib/receipt';
import { getMenuCache, refreshMenuCache, getMenuImages, refreshMenuImages } from '../../lib/menuCache';
import {
  Plus, Minus, Trash2, Send, Receipt, CreditCard, X, MoreVertical,
  ArrowRightLeft, Pencil, Combine, Ban, ChevronLeft, ArrowLeft, LayoutGrid, Printer, ChefHat, Wine,
} from 'lucide-react';

export const OrderDrawer = ({ open, onOpenChange, table, onChanged, onRequestChange, onRequestEdit }) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [view, setView] = useState('categories'); // 'categories' | 'subcategories' | 'products'
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [optProduct, setOptProduct] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stornoState, setStornoState] = useState(null); // { mode:'item'|'order', item? }
  const confirm = useConfirm();
  const { user, hasPerm } = useAuth();
  const canVoid = ['admin', 'manager'].includes(user?.role) || hasPerm('void');

  const tableId = table?.id;

  const loadOrder = async () => {
    if (!tableId) return;
    try {
      const { data } = await api.get(`/orders/by-table/${tableId}`);
      setOrder(data);
    } catch (e) {
      // No order yet: keep an empty local order WITHOUT creating one on the server,
      // so a free table stays free until the first product is actually added.
      setOrder({ id: null, items: [], total: 0 });
    }
  };

  // Cancel a freshly-opened but still empty order when closing, freeing the table
  const handleOpenChange = async (o) => {
    if (!o && order?.id && (order.items || []).length === 0) {
      try { await api.post(`/orders/${order.id}/cancel`); } catch (e) { /* ignore */ }
    }
    onOpenChange(o);
  };

  useEffect(() => {
    if (!open || !tableId) { setOrder(null); return; }
    setView('categories'); setSelectedCat(null); setSelectedSub(null);

    // Fonde le immagini (caricate a parte) nei prodotti già mostrati (lazy).
    const applyImages = (map) => {
      if (!map) return;
      setProducts((prev) => prev.map((p) => (map[p.id] ? { ...p, ...map[p.id] } : p)));
    };
    const loadImages = () => {
      const cachedImgs = getMenuImages('tavoli');
      if (cachedImgs) applyImages(cachedImgs);              // subito, se in cache memoria
      refreshMenuImages('tavoli').then(applyImages).catch(() => {}); // aggiorna in background
    };

    const cached = getMenuCache('tavoli');
    if (cached.hasData) {
      // Paint ISTANTANEO dalla cache: nessuno spinner per la griglia prodotti.
      setCategories(cached.categories); setProducts(cached.products); setSubcategories(cached.subcategories);
      setOrder({ id: null, items: [], total: 0 });
      setLoading(false);
      loadOrder();
      loadImages();
      // Revalidate testo/prezzi in background (stale-while-revalidate).
      refreshMenuCache('tavoli').then((fresh) => {
        setCategories(fresh.categories); setSubcategories(fresh.subcategories);
        setProducts(fresh.products);
        applyImages(getMenuImages('tavoli')); // riapplica le immagini sui prodotti aggiornati
      }).catch(() => {});
    } else {
      // Primo caricamento senza cache: mostra lo spinner una volta sola.
      setLoading(true);
      (async () => {
        try {
          const fresh = await refreshMenuCache('tavoli');
          setCategories(fresh.categories); setProducts(fresh.products); setSubcategories(fresh.subcategories);
        } catch (e) { /* noop */ }
        await loadOrder();
        setLoading(false);
        loadImages();
      })();
    }
  }, [open, tableId]); // eslint-disable-line

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.available);
    if (!selectedCat) return [];
    list = list.filter((p) => p.category_id === selectedCat.id);
    if (selectedSub) list = list.filter((p) => p.subcategory_id === selectedSub.id);
    return list;
  }, [products, selectedCat, selectedSub]);

  const countFor = (catId) => products.filter((p) => p.available && p.category_id === catId).length;
  const subsFor = (catId) => subcategories.filter((s) => s.category_id === catId);
  const countForSub = (subId) => products.filter((p) => p.available && p.subcategory_id === subId).length;

  // Navigazione: categoria -> (sottocategorie se presenti) -> prodotti
  const openCategory = (c) => {
    setSelectedCat(c);
    setSelectedSub(null);
    setView(subsFor(c.id).length > 0 ? 'subcategories' : 'products');
  };
  const openSub = (s) => { setSelectedSub(s); setView('products'); };
  const backFromProducts = () => {
    if (selectedCat && subsFor(selectedCat.id).length > 0) { setSelectedSub(null); setView('subcategories'); }
    else { setSelectedCat(null); setSelectedSub(null); setView('categories'); }
  };
  const backToCategories = () => { setSelectedCat(null); setSelectedSub(null); setView('categories'); };

  const addProduct = async (product, opts = {}) => {
    try {
      let ord = order;
      // Lazily create the order on the server the first time a product is added
      if (!ord?.id) {
        const { data } = await api.post('/orders/open', { table_id: tableId });
        ord = data;
        setOrder(data);
      }
      const { data } = await api.post(`/orders/${ord.id}/items`, {
        product_id: product.id, qty: opts.qty || 1,
        variant: opts.variant || null, extras: opts.extras || [], notes: opts.notes || '',
      });
      setOrder(data);
      onChanged && onChanged();
    } catch (e) { toast.error(t('order.addProductError')); }
  };

  const clickProduct = (product) => {
    if ((product.variants && product.variants.length) || (product.extras && product.extras.length)) setOptProduct(product);
    else addProduct(product);
  };

  const updateQty = async (item, delta) => {
    const nq = item.qty + delta;
    if (nq <= 0) return removeItem(item);
    const { data } = await api.put(`/orders/${order.id}/items/${item.id}`, { qty: nq });
    setOrder(data);
  };

  const removeItem = async (item, sent = false) => {
    // Storno di un prodotto GIÀ inviato in cucina/bar: richiede un MOTIVO.
    if (sent) {
      setStornoState({ mode: 'item', item });
      return;
    }
    try {
      const { data } = await api.delete(`/orders/${order.id}/items/${item.id}`);
      setOrder(data); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || t('order.cannotRemove')); }
  };

  // Conferma dello storno (prodotto o intero conto) con motivo obbligatorio.
  const confirmStorno = async (reason, note) => {
    const st = stornoState;
    try {
      const qs = `reason=${encodeURIComponent(reason)}${note ? `&reason_note=${encodeURIComponent(note)}` : ''}`;
      if (st?.mode === 'item') {
        const { data } = await api.delete(`/orders/${order.id}/items/${st.item.id}?${qs}`);
        setOrder(data); onChanged && onChanged();
        toast.success(t('order.itemVoided'));
      } else if (st?.mode === 'order') {
        await api.post(`/orders/${order.id}/cancel?${qs}`);
        toast.success(t('storno.orderVoided', { defaultValue: 'Conto stornato' }));
        onChanged && onChanged();
        onOpenChange(false);
      }
      setStornoState(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('order.cannotRemove'));
    }
  };

  // Ristampa comanda alla postazione (cucina/bar) senza rifare l'ordine.
  const reprintComanda = async (station) => {
    if (!order?.id) return;
    try {
      const { data } = await api.post('/print/comanda/reprint', { order_id: order.id, station });
      toast.success(t('order.comandaReprinted', { station, defaultValue: `Comanda ${station} ristampata` }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('order.reprintError', { defaultValue: 'Ristampa non riuscita' }));
    }
  };

  const sendKitchen = async () => {
    try {
      const { data } = await api.post(`/orders/${order.id}/send`);
      setOrder(data);
      toast.success(t('order.orderSent'));
      onChanged && onChanged();
      // Stampa automatica delle comande nelle stazioni (ePOS Wi-Fi o principale).
      claimAndPrintComande();
    }
    catch (e) { toast.error(e?.response?.data?.detail || t('order.noNewItems')); }
  };

  const requestBill = async () => {
    try {
      const via = await requestBillAndPrint(api, order.id);
      toast.success(via === 'agent' ? t('order.billSentToPrinter') : t('order.billPrinting'));
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('order.billError'));
    }
  };

  const onPaid = (res) => {
    if (res.order_closed) { toast.success(t('order.tableClosed')); onOpenChange(false); }
    onChanged && onChanged();
  };

  const unmerge = async () => {
    try { await api.post(`/tables/${table.id}/unmerge`); toast.success(t('order.tablesSeparated')); onChanged && onChanged(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || t('order.genericError')); }
  };

  // Unità NUOVE da inviare in preparazione = somma di (qty - sent_qty) su tutte le
  // righe. Così una riga già parzialmente inviata (es. x3 di cui 1 inviata) resta
  // inviabile per la parte nuova (2).
  const pendingCount = (order?.items || []).reduce(
    (s, i) => s + Math.max(0, Number(i.qty || 0) - Number(i.sent_qty || 0)), 0);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" hideClose noAnimation className="w-full sm:max-w-4xl lg:max-w-[76vw] p-0 flex flex-col gap-0" data-testid="order-drawer">
        {/* Header rimosso: titolo e azioni spostati nelle intestazioni delle due
            colonne (F6 a destra della riga "Categorie", ⋮/✕ sulla riga "Ordine"). */}

        {loading || !order ? (
          <div className="flex-1 flex flex-col">
            {/* ✕ sempre accessibile anche durante il caricamento */}
            <div className="flex items-center justify-end px-3 h-14 border-b border-border shrink-0">
              <Button variant="ghost" size="icon" onClick={() => handleOpenChange(false)} data-testid="drawer-close-loading"><X className="h-5 w-5" /></Button>
            </div>
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">{t('order.loadingOrder')}</div>
          </div>
        ) : (
          <div className="flex-1 grid grid-rows-[auto_1fr] lg:grid-rows-1 lg:grid-cols-[1fr_340px] overflow-hidden">
            {/* Categories / Products selector */}
            <div className="flex flex-col overflow-hidden border-b lg:border-b-0 lg:border-r border-border min-h-0">
              <div className="flex items-center gap-2 px-3 border-b border-border h-14 shrink-0">
                {view === 'products' ? (
                  <span className="font-semibold text-sm truncate">{selectedSub?.name || selectedCat?.name}</span>
                ) : view === 'subcategories' ? (
                  <span className="font-semibold text-sm truncate">{selectedCat?.name}</span>
                ) : (
                  <span className="font-semibold text-sm">{t('order.categories')}</span>
                )}
                {/* Titolo tavolo (F6) spostato qui: a destra, in fondo alla riga Categorie */}
                <div className="ml-auto text-right pl-2 shrink-0" data-testid="drawer-table-title">
                  <div className="font-display text-base font-semibold leading-tight text-primary">{table?.merged ? `${t('order.tablesPrefix')} ${table.merged_label}` : table?.name}</div>
                  <div className="text-[11px] text-muted-foreground leading-tight">{(order?.items || []).length} {t('order.items')}{table?.merged ? ` · ${t('order.mergedCount', { count: table.merged_count })}` : ''}</div>
                </div>
              </div>
              <ScrollArea className="flex-1 p-3">
                {/* Griglia responsive tramite STILE INLINE (non dipende dal purge di Tailwind
                    in produzione): colonne a larghezza minima così le card non si schiacciano
                    su schermi stretti/quadrati/touch e il testo resta leggibile. */}
                <div
                  className="grid gap-3"
                  style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}
                >
                  {view === 'categories' ? (
                    categories.length === 0
                      ? <div className="col-span-full text-center text-muted-foreground py-8 text-sm">{t('order.noCategories')}</div>
                      : categories.map((c) => (
                        <button key={c.id} data-testid="drawer-category" onClick={() => openCategory(c)}
                          className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left transition-all hover:border-primary hover:shadow-md active:scale-[0.98]">
                          <div className="relative w-full overflow-hidden bg-muted" style={{ aspectRatio: '4 / 3' }}>
                            {c.image ? (
                              <>
                                <img src={c.image} alt="" draggable={false} className="absolute inset-0 h-full w-full object-cover"
                                  style={{ objectPosition: `${c.image_x ?? 50}% ${c.image_y ?? 50}%`, transform: `scale(${c.image_zoom ?? 1})` }} />
                                <span className="absolute left-2 top-2 flex h-9 w-9 items-center justify-center rounded-lg bg-white/90 shadow-sm">
                                  <CategoryIcon iconKey={c.icon} color={c.color} className="h-5 w-5" />
                                </span>
                              </>
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <CategoryIcon iconKey={c.icon} color={c.color} className="h-12 w-12" />
                              </div>
                            )}
                          </div>
                          <div className="px-2.5 py-2">
                            <span className="block font-semibold text-sm leading-tight line-clamp-2">{c.name}</span>
                          </div>
                        </button>
                      ))
                  ) : view === 'subcategories' ? (
                    <>
                      <button data-testid="drawer-back-card" onClick={backToCategories}
                        className="group flex flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-slate-800 bg-slate-800 p-3 text-center transition-all hover:bg-slate-900 hover:shadow-md active:scale-[0.98]">
                        <ArrowLeft className="h-12 w-12 text-white transition-transform group-hover:-translate-x-0.5" />
                        <span className="font-display font-extrabold uppercase tracking-wide text-base leading-tight text-white">{t('order.back', { defaultValue: 'Indietro' })}</span>
                      </button>
                      <button data-testid="drawer-sub-all" onClick={() => openSub(null)}
                        className="group flex flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-border bg-card p-3 text-center transition-all hover:border-primary hover:shadow-md active:scale-[0.98]">
                        <LayoutGrid className="h-10 w-10 text-primary" />
                        <span className="font-semibold text-base leading-tight">{t('subcat.allOf', { defaultValue: 'Tutti' })}</span>
                      </button>
                      {subsFor(selectedCat?.id).map((s) => (
                        <button key={s.id} data-testid="drawer-subcategory" onClick={() => openSub(s)}
                          className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left transition-all hover:border-primary hover:shadow-md active:scale-[0.98]">
                          <div className="relative w-full overflow-hidden bg-muted" style={{ aspectRatio: '4 / 3' }}>
                            {s.image ? (
                              <img src={s.image} alt="" draggable={false} className="absolute inset-0 h-full w-full object-cover"
                                style={{ objectPosition: `${s.image_x ?? 50}% ${s.image_y ?? 50}%`, transform: `scale(${s.image_zoom ?? 1})` }} />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <CategoryIcon iconKey={s.icon} color={s.color} className="h-12 w-12" />
                              </div>
                            )}
                          </div>
                          <div className="px-2.5 py-2">
                            <span className="block font-semibold text-sm leading-tight line-clamp-2">{s.name}</span>
                          </div>
                        </button>
                      ))}
                    </>
                  ) : (
                    <>
                      <button data-testid="drawer-back-card" onClick={backFromProducts}
                        className="group flex flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-slate-800 bg-slate-800 p-3 text-center transition-all hover:bg-slate-900 hover:shadow-md active:scale-[0.98]">
                        <ArrowLeft className="h-12 w-12 text-white transition-transform group-hover:-translate-x-0.5" />
                        <span className="font-display font-extrabold uppercase tracking-wide text-base leading-tight text-white">{t('order.back', { defaultValue: 'Indietro' })}</span>
                      </button>
                      {filtered.length === 0
                        ? <div className="col-span-full text-center text-muted-foreground py-8 text-sm">{t('order.noProducts')}</div>
                        : filtered.map((p) => (
                        <button key={p.id} data-testid="drawer-product" onClick={() => clickProduct(p)}
                          className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left transition-all hover:border-primary hover:shadow-md active:scale-[0.98]">
                          <div className="relative w-full overflow-hidden bg-muted" style={{ aspectRatio: '4 / 3' }}>
                            {p.image ? (
                              <img src={p.image} alt="" draggable={false} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover"
                                style={{ objectPosition: `${p.image_x ?? 50}% ${p.image_y ?? 50}%`, transform: `scale(${p.image_zoom ?? 1})` }} />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <CategoryIcon iconKey={selectedCat?.icon} color={selectedCat?.color} className="h-8 w-8 opacity-40" />
                              </div>
                            )}
                            <span className="absolute left-2 top-2 rounded-full bg-background/85 backdrop-blur-sm px-2.5 py-1 text-sm font-bold text-primary shadow-sm-token" data-testid="drawer-product-price">{fmtEur(p.price)}</span>
                          </div>
                          <div className="px-2.5 py-2">
                            <span className="block font-semibold text-sm leading-tight line-clamp-2">{p.name}</span>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Cart */}
            <div className="flex flex-col overflow-hidden min-h-0">
              <div className="flex items-center gap-2 px-4 border-b border-border h-14 shrink-0">
                <span className="font-semibold text-base">{t('order.order')}</span>
                {/* Azioni (⋮) e chiusura (✕) spostate qui, a destra della riga Ordine */}
                <div className="ml-auto flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid="drawer-actions-button"><MoreVertical className="h-5 w-5" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {table?.merged && (
                        <DropdownMenuItem onClick={unmerge} data-testid="drawer-unmerge"><Combine className="h-4 w-4 mr-2" /> {t('order.separateTables')}</DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => { onOpenChange(false); onRequestChange && onRequestChange(table); }}><ArrowRightLeft className="h-4 w-4 mr-2" /> {t('order.changeTable')}</DropdownMenuItem>
                      {!table?.merged && (
                        <DropdownMenuItem onClick={() => { onOpenChange(false); onRequestEdit && onRequestEdit(table); }}><Pencil className="h-4 w-4 mr-2" /> {t('order.editTable')}</DropdownMenuItem>
                      )}
                      {(order.items || []).some((it) => ['preparing', 'ready'].includes(it.kitchen_status)) && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => reprintComanda('cucina')} data-testid="drawer-reprint-cucina"><ChefHat className="h-4 w-4 mr-2" /> {t('order.reprintKitchen', { defaultValue: 'Ristampa comanda cucina' })}</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => reprintComanda('bar')} data-testid="drawer-reprint-bar"><Wine className="h-4 w-4 mr-2" /> {t('order.reprintBar', { defaultValue: 'Ristampa comanda bar' })}</DropdownMenuItem>
                        </>
                      )}
                      {(order.items || []).length > 0 && canVoid && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setStornoState({ mode: 'order' })} data-testid="drawer-void-order"><Ban className="h-4 w-4 mr-2" /> {t('order.voidOrder', { defaultValue: 'Storna conto' })}</DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="icon" onClick={() => handleOpenChange(false)} data-testid="drawer-close"><X className="h-5 w-5" /></Button>
                </div>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-3 space-y-2">
                  {(order.items || []).length === 0 && <div className="text-center text-muted-foreground py-10 text-sm">{t('order.noItems')}</div>}
                  {(order.items || []).map((it) => {
                    const ks = KITCHEN_STATUS[it.kitchen_status] || KITCHEN_STATUS.pending;
                    const sentQty = Number(it.sent_qty || 0);
                    const hasPending = Number(it.qty || 0) > sentQty;   // c'è una parte nuova non inviata
                    const anySent = sentQty > 0;                        // parte già inviata -> serve storno
                    return (
                      <div key={it.id} className="rounded-xl border border-border p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-base">{it.name}{it.variant ? ` (${it.variant})` : ''}</div>
                            {it.extras?.length > 0 && <div className="text-sm text-muted-foreground">+ {it.extras.map((e) => e.name).join(', ')}</div>}
                            {it.notes && <div className="text-sm text-accent">{t('order.note')}: {it.notes}</div>}
                            <div className={cn('text-xs mt-0.5', ks.color)}>{kitchenStatusLabel(t, it.kitchen_status)}</div>
                            {anySent && hasPending && (
                              <div className="text-[11px] text-muted-foreground" data-testid="drawer-item-partial">{sentQty} {t('order.sentQty', { defaultValue: 'inviati' })} · {Number(it.qty) - sentQty} {t('order.newQty', { defaultValue: 'nuovi' })}</div>
                            )}
                            {Number(it.line_discount_amount || 0) > 0 && (
                              <div className="text-xs text-primary" data-testid="drawer-item-discount">{t('itemEdit.discount', { defaultValue: 'Sconto' })}: -{fmtEur(it.line_discount_amount)}</div>
                            )}
                            {it.price_overridden && (
                              <div className="text-xs text-muted-foreground">{t('itemEdit.priceModified', { defaultValue: 'Prezzo modificato' })}</div>
                            )}
                          </div>
                          <div className="font-display font-semibold tabular text-base">{fmtEur(it.line_total)}</div>
                        </div>
                        <div className="flex items-center justify-between mt-2.5">
                          <div className="flex items-center gap-1.5">
                            <Button variant="outline" size="icon" className="h-9 w-9" disabled={!hasPending} onClick={() => updateQty(it, -1)} data-testid="drawer-qty-minus"><Minus className="h-4 w-4" /></Button>
                            <span className="w-9 text-center font-semibold tabular text-base" data-testid="drawer-qty">{it.qty}</span>
                            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => updateQty(it, 1)} data-testid="drawer-qty-plus"><Plus className="h-4 w-4" /></Button>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-primary" onClick={() => setEditItem(it)} data-testid="drawer-item-edit" title={t('itemEdit.title', { defaultValue: 'Modifica articolo' })}><Pencil className="h-4 w-4" /></Button>
                            {anySent
                              ? (canVoid ? <Button variant="ghost" size="sm" className="h-9 px-3 text-destructive hover:text-destructive" data-testid="drawer-void-item" onClick={() => removeItem(it, true)}><Ban className="h-4 w-4 mr-1" /> {t('order.void')}</Button> : null)
                              : <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeItem(it)}><Trash2 className="h-4 w-4" /></Button>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
              <div className="border-t border-border p-3 space-y-3">
                {/* Controllo sconto: ora disponibile anche nella vista a modale del tavolo
                    (prima era presente solo nella pagina intera dell'ordine). */}
                <DiscountControl order={order} onApplied={(o) => setOrder((prev) => ({ ...prev, ...o }))} className="pb-3 border-b border-border" />
                {Number(order.discount_amount || 0) > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t('payment.subtotal', { defaultValue: 'Subtotale' })}</span>
                    <span className="tabular text-muted-foreground line-through">{fmtEur(order.items_total ?? order.total)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-base">{t('order.total')}</span>
                  <span className="font-display text-2xl font-semibold tabular">{fmtEur(order.total)}</span>
                </div>
                <Button onClick={sendKitchen} disabled={pendingCount === 0} className="w-full h-14 rounded-xl text-base" data-testid="drawer-send-kitchen">
                  <Send className="h-5 w-5 mr-2" /> {t('order.sendToKitchen')} {pendingCount > 0 ? `(${pendingCount})` : ''}
                </Button>
                <div className="grid grid-cols-2 gap-2.5">
                  <Button variant="outline" onClick={requestBill} disabled={(order.items || []).length === 0} className="h-12 rounded-xl text-base" data-testid="drawer-request-bill"><Receipt className="h-5 w-5 mr-2" /> {t('order.bill')}</Button>
                  <Button onClick={() => setPayOpen(true)} disabled={(order.items || []).length === 0} className="h-12 rounded-xl text-base" data-testid="drawer-pay"><CreditCard className="h-5 w-5 mr-2" /> {t('order.pay')}</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <DrawerProductOptions product={optProduct} onClose={() => setOptProduct(null)} onConfirm={(opts) => { addProduct(optProduct, opts); setOptProduct(null); }} />
        <ItemEditDialog orderId={order?.id} item={editItem} open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)} onUpdated={(data) => setOrder(data)} />
        <PaymentDialog open={payOpen} onOpenChange={setPayOpen} order={order} onPaid={onPaid} />
        <StornoReasonDialog
          open={!!stornoState}
          onOpenChange={(o) => !o && setStornoState(null)}
          onConfirm={confirmStorno}
          itemName={stornoState?.mode === 'item' ? stornoState.item?.name : undefined}
          title={stornoState?.mode === 'order' ? t('storno.orderTitle', { defaultValue: 'Storna intero conto' }) : undefined}
          description={stornoState?.mode === 'order' ? t('storno.orderDesc', { defaultValue: 'Seleziona il motivo per stornare tutto il conto.' }) : undefined}
        />
      </SheetContent>
    </Sheet>
  );
};

function DrawerProductOptions({ product, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [variant, setVariant] = useState(null);
  const [extras, setExtras] = useState({});
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (product) { setVariant(product.variants?.[0]?.name || null); setExtras({}); setQty(1); setNotes(''); }
  }, [product]);

  if (!product) return null;
  const selectedExtras = (product.extras || []).filter((e) => extras[e.name]).map((e) => ({ name: e.name, price: e.price }));

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent noAnimation className="max-w-md">
        <DialogHeader><DialogTitle>{product.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {product.variants?.length > 0 && (
            <div>
              <Label>{t('order.variant')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {product.variants.map((v) => (
                  <button key={v.name} onClick={() => setVariant(v.name)} className={cn('h-11 rounded-lg border text-sm font-medium transition-colors', variant === v.name ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted')}>
                    {v.name} {v.price_delta ? `(+${fmtEur(v.price_delta)})` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
          {product.extras?.length > 0 && (
            <div>
              <Label>{t('order.extras')}</Label>
              <div className="space-y-1.5 mt-1.5">
                {product.extras.map((e) => (
                  <label key={e.name} className="flex items-center justify-between h-10 px-3 rounded-lg border border-border cursor-pointer hover:bg-muted">
                    <span className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!extras[e.name]} onChange={(ev) => setExtras((s) => ({ ...s, [e.name]: ev.target.checked }))} /> {e.name}</span>
                    <span className="text-sm text-muted-foreground">+{fmtEur(e.price)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div><Label>{t('order.kitchenNotes')}</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('order.kitchenNotesPlaceholder')} className="mt-1.5" /></div>
          <div className="flex items-center gap-2">
            <Label className="mr-2">{t('order.quantity')}</Label>
            <Button variant="outline" size="icon" onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus className="h-4 w-4" /></Button>
            <span className="w-8 text-center font-medium">{qty}</span>
            <Button variant="outline" size="icon" onClick={() => setQty((q) => q + 1)}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => onConfirm({ variant, extras: selectedExtras, qty, notes })} data-testid="drawer-add-product">{t('common.add')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
